import React from 'react';
import { toast } from 'react-toastify';
import i18n from 'src/utils/i18n';

export type UiToastType = (
  content: Parameters<typeof toast>[0],
  options?: Parameters<typeof toast>[1]
) => ReturnType<typeof toast>;

const ToastIcon = (type: 'success' | 'error' | 'info' | 'closeBtn') => {
  if (type === 'success') {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* eslint-disable-next-line max-len */}
        <path d="M11 0.5C8.9233 0.5 6.89323 1.11581 5.16652 2.26957C3.4398 3.42332 2.09399 5.0632 1.29927 6.98182C0.504549 8.90045 0.296614 11.0116 0.701759 13.0484C1.1069 15.0852 2.10693 16.9562 3.57538 18.4246C5.04383 19.8931 6.91476 20.8931 8.95156 21.2982C10.9884 21.7034 13.0996 21.4955 15.0182 20.7007C16.9368 19.906 18.5767 18.5602 19.7304 16.8335C20.8842 15.1068 21.5 13.0767 21.5 11C21.5 8.21523 20.3938 5.54451 18.4246 3.57538C16.4555 1.60625 13.7848 0.5 11 0.5V0.5ZM9.50001 15.1925L5.75 11.4425L6.94251 10.25L9.50001 12.8075L15.0575 7.25L16.2545 8.4395L9.50001 15.1925Z" />
      </svg>
    );
  }
  if (type === 'error') {
    return (
      <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* eslint-disable-next-line max-len */}
        <path d="M23.559 16.6908L14.3669 2.28758C13.8485 1.47524 12.9637 0.990234 12 0.990234C11.0362 0.990234 10.1514 1.47524 9.63316 2.28758L0.440981 16.6907C-0.110746 17.5553 -0.147348 18.6513 0.34577 19.551C0.838888 20.4504 1.78234 21.0092 2.80796 21.0092H21.1921C22.2177 21.0092 23.1612 20.4505 23.6544 19.5508C24.1473 18.6513 24.1107 17.5554 23.559 16.6908ZM12 18.5847C11.1547 18.5847 10.4695 17.8995 10.4695 17.0542C10.4695 16.2089 11.1548 15.5237 12 15.5237C12.8453 15.5237 13.5305 16.2089 13.5305 17.0542C13.5305 17.8995 12.8452 18.5847 12 18.5847ZM14.1213 7.94933L13.3675 13.0247C13.2566 13.7718 12.561 14.2874 11.814 14.1765C11.2104 14.0868 10.7579 13.6136 10.6649 13.042L9.84628 7.97666C9.65571 6.7973 10.4572 5.68675 11.6366 5.49617C12.816 5.30559 13.9265 6.10712 14.1171 7.28648C14.1529 7.50856 14.1517 7.73783 14.1213 7.94933Z" />
      </svg>
    );
  }
  if (type === 'info') {
    return (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* eslint-disable-next-line max-len */}
        <path d="M11 0.34375C5.11513 0.34375 0.34375 5.11685 0.34375 11C0.34375 16.8866 5.11513 21.6562 11 21.6562C16.8849 21.6562 21.6562 16.8866 21.6562 11C21.6562 5.11685 16.8849 0.34375 11 0.34375ZM11 5.07031C11.9967 5.07031 12.8047 5.8783 12.8047 6.875C12.8047 7.8717 11.9967 8.67969 11 8.67969C10.0033 8.67969 9.19531 7.8717 9.19531 6.875C9.19531 5.8783 10.0033 5.07031 11 5.07031ZM13.4062 15.9844C13.4062 16.2691 13.1754 16.5 12.8906 16.5H9.10938C8.82462 16.5 8.59375 16.2691 8.59375 15.9844V14.9531C8.59375 14.6684 8.82462 14.4375 9.10938 14.4375H9.625V11.6875H9.10938C8.82462 11.6875 8.59375 11.4566 8.59375 11.1719V10.1406C8.59375 9.85587 8.82462 9.625 9.10938 9.625H11.8594C12.1441 9.625 12.375 9.85587 12.375 10.1406V14.4375H12.8906C13.1754 14.4375 13.4062 14.6684 13.4062 14.9531V15.9844Z" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.544 14.5455L1.45312 1.45459M14.544 1.45459L1.45312 14.5455"
        stroke="white"
        strokeWidth="1.6994"
        strokeLinecap="round"
      />
    </svg>
  );
};

const CloseButton = ({ closeToast }: { closeToast: () => void }) => (
  <button className="Toastify__close-button Toastify__close-button--colored" type="button" onClick={closeToast}>
    {ToastIcon('closeBtn')}
  </button>
);

const BaseToast =
  (type: 'success' | 'error' | 'info'): UiToastType =>
  (content, options) =>
    toast[type](content, {
      ...options,
      closeButton: CloseButton,
      icon: ToastIcon(type),
    });

export const UiToast = {
  success: BaseToast('success'),
  error: BaseToast('error'),
  info: BaseToast('info'),
  commonSuccessSaved: (): string | number => BaseToast('success')(i18n.t('common.saved') as string),
  commonSuccessDeleted: (): string | number => BaseToast('success')(i18n.t('common.deleted') as string),
  commonSuccessResendInvitation: (): string | number =>
    BaseToast('success')(i18n.t('common.resendInvitation.success') as string),
  commonErrorResendInvitation: (): string | number =>
    BaseToast('error')(i18n.t('common.resendInvitation.error') as string),
};

/** outer API for the knockout usage */
window.toast = {
  success: BaseToast('success'),
  error: BaseToast('error'),
  info: BaseToast('info'),
};
